import { AgentDock } from "@/components/agent-dock";
import { TargetHighlight } from "@/components/target-highlight";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { MissionMark } from "@/components/mission-chrome";
import { SessionUserSkeleton, SidebarListSkeleton } from "@/components/mission-skeletons";
import { ProjectUnderlineNav } from "@/components/underline-nav";
import { fetchSessionUser, shortOid, signOutSession, type Issue, type Job, type Review, type SessionUser } from "@/lib/api";
import { countOpenChanges } from "@/lib/changes-list";
import { jobTypeLabel } from "@/lib/job-kind-labels";
import { summarizeJobActivity } from "@/lib/job-drain";
import {
  MISSION_CRUMB_ORG_BTN_CLASS,
  MISSION_CRUMB_ORG_LABEL_CLASS,
  MISSION_CRUMB_PROJECT_BTN_CLASS,
  MISSION_CRUMB_PROJECT_LABEL_CLASS,
  MISSION_CRUMB_PROJECT_WRAP_CLASS,
  MISSION_CRUMB_WRAP_CLASS,
} from "@/lib/mission-crumb";
import {
  MISSION_COPPER_LINE,
  getMissionDrive,
  isHumanTakeoverEvent,
  isMissionDriveActive,
  pauseMissionDrive,
  subscribeMissionDrive,
} from "@/lib/mission-drive";
import { ProjectScopeProvider, useProjectScope } from "@/lib/project-scope";
import {
  OPEN_THINK_EVENT,
  PROJECT_TABS,
  changesHref,
  projectSwitchHref,
  projectTabHref,
  thinkOpensByDefault,
} from "@/lib/underline-nav";
import { THINK_DOCK, thinkSheetOverlayOpen, thinkUsesSheet } from "@/lib/think-dock-layout";
import { isProjectMissionBase, missionBrandHref, missionStageHref } from "@/lib/workflow";
import { openIssueCount } from "@/lib/collaboration";
import { cn } from "@/lib/utils";
import gsap from "gsap";
import {
  Activity,
  ChevronDown,
  Flag,
  GitBranch,
  Menu,
  Search,
  Settings,
  Terminal,
  Users,
} from "lucide-react";
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, NavLink, useLocation, useNavigate, useParams } from "react-router-dom";

const THINK_PREF = "mx.think";

function readThinkPref(): boolean | null {
  try {
    const value = sessionStorage.getItem(THINK_PREF);
    if (value === "1") return true;
    if (value === "0") return false;
  } catch {
    /* ignore */
  }
  return null;
}

function writeThinkPref(open: boolean) {
  try {
    sessionStorage.setItem(THINK_PREF, open ? "1" : "0");
  } catch {
    /* ignore */
  }
}

function wfClass(on: boolean) {
  return cn(
    "block rounded-[4px] border-l-2 border-transparent px-3 py-2 text-[13px] no-underline",
    "text-muted-foreground hover:bg-white/[0.08] hover:text-foreground",
    on && "border-falcon bg-white/[0.08] text-foreground",
  );
}

function OrgScopeNav({
  orgBase,
  orgName,
  projects,
  loading,
  onNavigate,
}: {
  orgBase: string;
  orgName: string;
  projects: { id: string; slug: string; name: string }[];
  loading?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col px-2 py-3" data-testid="org-nav">
      <div className="px-3 pb-2">
        <div className="text-[10px] font-semibold tracking-[0.12em] text-[#545457] uppercase">Organization</div>
        <div className="mt-0.5 truncate text-sm font-medium">{orgName}</div>
      </div>
      <nav className="flex flex-col gap-0.5">
        <NavLink to={orgBase} end onClick={onNavigate} className={({ isActive }) => wfClass(isActive)}>
          Overview
        </NavLink>
        <Link to={`${orgBase}#members`} onClick={onNavigate} className={wfClass(false)}>
          Members
        </Link>
      </nav>
      <div className="mt-3 px-3 pb-1 text-[10px] font-semibold tracking-[0.12em] text-[#545457] uppercase">
        Projects
      </div>
      <nav className="flex flex-col gap-0.5">
        {loading ? (
          <SidebarListSkeleton rows={3} testId="sidebar-projects-skeleton" />
        ) : (
          projects.map((p) => (
            <NavLink
              key={p.id || p.slug}
              to={`${orgBase}/p/${p.slug}/files`}
              onClick={onNavigate}
              className={({ isActive }) => wfClass(isActive)}
            >
              <span className="min-w-0 flex-1 truncate">{p.name || p.slug}</span>
            </NavLink>
          ))
        )}
      </nav>
      <p className="mt-auto px-3 pt-4 pb-3 font-mono text-[10px] text-[#545457]">Team Platform</p>
    </div>
  );
}

function CommandSearch({
  open,
  onOpenChange,
  projectBase,
  orgBase,
  orgName,
  issues,
  reviews,
  jobs,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectBase: string | null;
  orgBase: string;
  orgName: string;
  issues: Issue[];
  reviews: Review[];
  jobs: Job[];
}) {
  const navigate = useNavigate();
  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Search"
      description="Jump to a page, issue, review, or job"
      className="border-border bg-card shadow-2xl"
    >
      <CommandInput placeholder="Search oid, path, issue…" />
      <CommandList>
        <CommandEmpty>No match.</CommandEmpty>
        {isProjectMissionBase(projectBase) && (
          <CommandGroup heading="Project">
            {PROJECT_TABS.map((tab) => (
              <CommandItem key={tab.id} onSelect={() => go(projectTabHref(projectBase, tab.to))}>
                {tab.label}
              </CommandItem>
            ))}
            <CommandItem onSelect={() => go(`${projectBase}/jobs`)}>
              <Terminal /> Jobs
            </CommandItem>
            <CommandItem onSelect={() => go(`${projectBase}/milestones`)}>
              <Flag /> Milestones
            </CommandItem>
            <CommandItem onSelect={() => go(`${projectBase}/settings/mcp`)}>
              <Settings /> MCP / Agent
            </CommandItem>
          </CommandGroup>
        )}
        <CommandGroup heading="Organization">
          <CommandItem onSelect={() => go(orgBase)}>
            <Users /> {orgName}
          </CommandItem>
        </CommandGroup>
        {isProjectMissionBase(projectBase) && issues.length > 0 && (
          <CommandGroup heading="Issues">
            {issues.map((issue) => (
              <CommandItem key={issue.id} onSelect={() => go(missionStageHref(projectBase, `issues/${issue.id}`))}>
                <Flag />
                <span>
                  {issue.number != null ? `I-${issue.number} ` : ""}
                  {issue.title}
                </span>
                <span className="ml-auto font-mono text-[11px] text-muted-foreground">{issue.git_ref}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {isProjectMissionBase(projectBase) && reviews.length > 0 && (
          <CommandGroup heading="Changes">
            {reviews.map((review) => (
              <CommandItem key={review.id} onSelect={() => go(changesHref(projectBase, review))}>
                <GitBranch />
                {review.title || `${review.from_ref} → ${review.to_ref}`}
                <span className="ml-auto font-mono text-[11px] text-muted-foreground">
                  {shortOid(review.from_ref)} ↔ {shortOid(review.to_ref)}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {isProjectMissionBase(projectBase) && jobs.length > 0 && (
          <CommandGroup heading="Jobs">
            {jobs.map((job) => (
              <CommandItem key={job.id} onSelect={() => go(missionStageHref(projectBase, `jobs/${job.id}`))}>
                <Terminal />
                {jobTypeLabel(job.kind, job.kind_detail)}
                <span className="ml-auto font-mono text-[11px] text-muted-foreground">{job.state}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}

type ProjectChromeValue = {
  title?: ReactNode;
  showAgent: boolean;
  contentClassName?: string;
  setTitle: (title?: ReactNode) => void;
  setShowAgent: (show: boolean) => void;
  setContentClassName: (className?: string) => void;
};

const ProjectChromeContext = createContext<ProjectChromeValue | null>(null);

export function ProjectChromeProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<ReactNode>();
  const [showAgent, setShowAgent] = useState(true);
  const [contentClassName, setContentClassName] = useState<string | undefined>();
  const value = useMemo<ProjectChromeValue>(
    () => ({ title, showAgent, contentClassName, setTitle, setShowAgent, setContentClassName }),
    [title, showAgent, contentClassName],
  );
  return <ProjectChromeContext.Provider value={value}>{children}</ProjectChromeContext.Provider>;
}

function NestedAppShell({
  title,
  children,
  showAgent = true,
  contentClassName,
}: {
  title?: ReactNode;
  children: ReactNode;
  showAgent?: boolean;
  contentClassName?: string;
}) {
  const chrome = useContext(ProjectChromeContext);
  useLayoutEffect(() => {
    if (!chrome) return;
    chrome.setTitle(title);
    chrome.setShowAgent(showAgent);
    chrome.setContentClassName(contentClassName);
  }, [chrome, title, showAgent, contentClassName]);
  return <>{children}</>;
}

export function AppShell(props: {
  title?: ReactNode;
  children: ReactNode;
  showAgent?: boolean;
  contentClassName?: string;
}) {
  const chrome = useContext(ProjectChromeContext);
  if (chrome) return <NestedAppShell {...props} />;
  return (
    <ProjectScopeProvider>
      <AppShellInner {...props} />
    </ProjectScopeProvider>
  );
}

/** Persistent project chrome. Used by ProjectLayout so useAgent survives tab changes. */
export function AppShellFrame({ children }: { children: ReactNode }) {
  const chrome = useContext(ProjectChromeContext);
  return (
    <AppShellInner
      title={chrome?.title}
      showAgent={chrome?.showAgent ?? true}
      contentClassName={chrome?.contentClassName}
    >
      {children}
    </AppShellInner>
  );
}

function AppShellInner({
  title,
  children,
  showAgent = true,
  contentClassName,
}: {
  title?: ReactNode;
  children: ReactNode;
  showAgent?: boolean;
  contentClassName?: string;
}) {
  const root = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const ctx = useProjectScope();
  const orgBase = ctx.orgBase || (ctx.orgSlug ? `/o/${ctx.orgSlug}` : "/");
  const projectBase = ctx.projectBase || orgBase;
  const orgName = ctx.org?.name || ctx.orgSlug || "org";
  const projectName = ctx.project?.name || ctx.projectSlug || "project";
  const scope: "org" | "project" = /\/p\//.test(location.pathname) ? "project" : "org";
  const thinkDefault = thinkOpensByDefault(location.pathname);
  const [jobActivity, setJobActivity] = useState(() => summarizeJobActivity([]));
  const [navOpen, setNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [agentOpen, setAgentOpen] = useState(() => {
    const pref = readThinkPref();
    if (pref !== null) return pref;
    return thinkOpensByDefault(window.location.pathname);
  });
  const [mobileAgent, setMobileAgent] = useState(false);
  const [viewport, setViewport] = useState(() =>
    typeof window === "undefined" ? THINK_DOCK.sheetBelow : window.innerWidth,
  );
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(null);
  const [authMode, setAuthMode] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [driveSnap, setDriveSnap] = useState(() => getMissionDrive());
  const thinkDriving = driveSnap.driving && !driveSnap.paused;
  const nav = useNavigate();
  const displayUser = sessionUser;
  const displayName = displayUser?.name || displayUser?.email || "Guest";
  const displayEmail = displayUser?.email || "";
  const displayInitials = (displayUser?.name || displayUser?.email || "?").slice(0, 2).toUpperCase();

  useEffect(() => {
    if (!root.current) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    gsap.fromTo(root.current, { opacity: 0 }, { opacity: 1, duration: 0.22, ease: "power2.out" });
  }, []);

  useEffect(() => {
    setJobActivity(summarizeJobActivity(ctx.jobs));
  }, [ctx.jobs]);

  useEffect(() => {
    const pref = readThinkPref();
    if (pref !== null) {
      setAgentOpen(pref);
      return;
    }
    setAgentOpen(thinkDefault);
  }, [thinkDefault]);

  useEffect(() => {
    const sync = () => setViewport(window.innerWidth);
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  useEffect(() => {
    const onOpen = () => {
      writeThinkPref(true);
      setAgentOpen(true);
      if (thinkUsesSheet(window.innerWidth)) {
        setMobileAgent(true);
      }
    };
    window.addEventListener(OPEN_THINK_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_THINK_EVENT, onOpen);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      fetchSessionUser().catch(() => null),
      fetch("/api/health", { credentials: "include" })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null),
    ]).then(([user, health]: [SessionUser | null, { auth_mode?: string } | null]) => {
      if (cancelled) return;
      const mode = health?.auth_mode ?? null;
      if (mode) setAuthMode(mode);
      if (user) {
        setSessionUser(user);
        setSessionReady(true);
        return;
      }
      setSessionReady(true);
      // Staging bypass: stay on the workbench. Do not bounce /login ↔ /o/pcbx.
      if (mode === "auth_bypass") return;
      nav("/login");
    });
    return () => {
      cancelled = true;
    };
  }, [nav]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === "Escape" && isMissionDriveActive() && isHumanTakeoverEvent(event)) {
        event.preventDefault();
        pauseMissionDrive("esc");
      }
    };
    const onPageClick = (event: MouseEvent) => {
      if (!isMissionDriveActive()) return;
      if (!isHumanTakeoverEvent(event)) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("[data-think-dock], [data-testid='think-dock'], [data-testid='page-agent-bar']")) return;
      pauseMissionDrive("click");
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("click", onPageClick, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("click", onPageClick, true);
    };
  }, []);

  useEffect(() => subscribeMissionDrive(setDriveSnap), []);

  const setThinkOpen = (open: boolean) => {
    writeThinkPref(open);
    setAgentOpen(open);
    if (!open) {
      root.current?.style.removeProperty("--think-w");
    }
  };

  const logoHref = missionBrandHref(orgBase);
  const orgNavProps = {
    orgBase,
    orgName,
    projects: ctx.projects,
    loading: ctx.loading,
  } as const;

  return (
    <div
      ref={root}
      className={cn("app-shell flex h-full flex-col overflow-hidden", agentOpen && showAgent && "think-open")}
    >
      {thinkDriving ? (
        <div
          className="pointer-events-none fixed inset-x-0 top-0 z-50"
          data-testid="think-driving-line"
        >
          <div className="h-0.5 w-full" style={{ background: MISSION_COPPER_LINE }} />
        </div>
      ) : null}
      <header
        data-testid="mission-topbar"
        className="platform-topbar sticky top-0 z-40 flex h-12 shrink-0 items-center gap-3.5 border-b border-border px-4"
      >
        {scope === "org" && (
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            aria-label="Open navigation"
            onClick={() => setNavOpen(true)}
          >
            <Menu className="size-4" />
          </Button>
        )}

        <Link
          to={logoHref}
          data-testid="mission-brand"
          className="flex shrink-0 items-center gap-2.5 text-[13px] font-semibold tracking-[0.04em] text-foreground uppercase no-underline"
        >
          <MissionMark size={22} />
          <span className="hidden sm:inline">PCBX</span>
        </Link>

        <div className={MISSION_CRUMB_WRAP_CLASS} data-testid="mission-crumb">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                title={orgName}
                data-testid="mission-crumb-org"
                className={MISSION_CRUMB_ORG_BTN_CLASS}
              >
                <span className={MISSION_CRUMB_ORG_LABEL_CLASS}>{orgName}</span>
                <ChevronDown className="size-3 shrink-0 opacity-60 lg:hidden" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel className="text-[11px] text-muted-foreground">Organization</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to={orgBase}>{orgName}</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {scope === "project" && (
            <>
              <span className="text-muted-foreground/70" aria-hidden>
                /
              </span>
              <div className={MISSION_CRUMB_PROJECT_WRAP_CLASS}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    title={projectName}
                    data-testid="mission-crumb-project"
                    className={MISSION_CRUMB_PROJECT_BTN_CLASS}
                  >
                    <span className={MISSION_CRUMB_PROJECT_LABEL_CLASS}>{projectName}</span>
                    <ChevronDown className="size-3 shrink-0 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel className="text-[11px] text-muted-foreground">Project</DropdownMenuLabel>
                  {ctx.projects.map((p) => (
                    <DropdownMenuItem key={p.id || p.slug} asChild>
                      <Link
                        to={projectSwitchHref(orgBase, p.slug, location.pathname)}
                        title={p.name || p.slug}
                      >
                        {p.name || p.slug}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  {ctx.projects.length === 0 && !ctx.loading && (
                    <DropdownMenuItem asChild>
                      <Link to={projectTabHref(projectBase, "files")}>{projectName}</Link>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              </div>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="mx-auto hidden h-8 min-w-0 flex-1 items-center gap-2 rounded-full border border-border bg-card px-3.5 text-left text-[13px] text-muted-foreground transition-colors hover:bg-white/[0.08] hover:text-foreground lg:flex lg:max-w-[420px]"
        >
          <Search className="size-3.5 shrink-0" />
          <span className="min-w-0 flex-1 truncate">Search oid, path, issue…</span>
          <kbd className="rounded-full border border-border px-1.5 py-px font-mono text-[11px] text-[#545457]">
            ⌘K
          </kbd>
        </button>

        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            aria-label="Search"
            onClick={() => setSearchOpen(true)}
          >
            <Search className="size-4" />
          </Button>

          <Link
            to={ctx.projectSlug ? `${projectBase}/jobs` : orgBase}
            className="relative hidden items-center p-1 text-muted-foreground no-underline hover:text-foreground sm:inline-flex"
            aria-label={jobActivity.ariaLabel}
            title={jobActivity.active > 0 ? jobActivity.ariaLabel : "Jobs"}
          >
            <Activity className="size-4" />
            {jobActivity.badgeCount > 0 && (
              <span className="job-pulse absolute -top-0.5 -right-0.5 grid size-4 place-items-center rounded-full bg-falcon font-mono text-[9px] font-bold text-white">
                {jobActivity.badgeCount}
              </span>
            )}
          </Link>

          {showAgent && (
            <Button
              variant="ghost"
              size="icon-sm"
              className="xl:hidden"
              aria-label="Open Think"
              onClick={() => setMobileAgent(true)}
            >
              <span className="grid size-7 place-items-center rounded-[2px] border border-border font-mono text-[9px] font-bold tracking-[0.06em] text-foreground">
                TH
              </span>
            </Button>
          )}

          {sessionReady ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 gap-2 px-1.5 text-[12px] tracking-[0.02em] text-muted-foreground sm:px-2">
                  <Avatar size="sm">
                    <AvatarFallback className="bg-muted font-mono text-[10px] text-foreground">
                      {displayInitials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline">{displayName}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>
                  <div className="font-medium text-foreground">{displayName}</div>
                  <div className="font-mono text-[11px] font-normal text-muted-foreground">{displayEmail || "no email"}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled>
                  {authMode === "auth_bypass" || sessionUser?.id === "user_auth_bypass"
                    ? "Auth bypass — no login"
                    : authMode === "better_auth"
                      ? "Better Auth"
                      : authMode === "cookie_demo"
                        ? "Local cookie demo — not Better Auth"
                        : authMode
                          ? "Auth unbound"
                          : "Auth loading"}
                </DropdownMenuItem>
                {authMode === "auth_bypass" || sessionUser?.id === "user_auth_bypass" || !sessionUser ? (
                  <DropdownMenuItem disabled>
                    {sessionUser?.id === "user_auth_bypass" || authMode === "auth_bypass"
                      ? "Sign out is off on staging/dev bypass"
                      : "No session"}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onSelect={() => {
                      void signOutSession().finally(() => nav("/login"));
                    }}
                  >
                    Sign out
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <SessionUserSkeleton />
          )}
        </div>
      </header>

      <CommandSearch
        open={searchOpen}
        onOpenChange={setSearchOpen}
        projectBase={ctx.projectBase}
        orgBase={orgBase}
        orgName={orgName}
        issues={ctx.issues}
        reviews={ctx.reviews}
        jobs={ctx.jobs}
      />

      {scope === "org" && (
        <Sheet open={navOpen} onOpenChange={setNavOpen}>
          <SheetContent
            side="left"
            showCloseButton={false}
            className="w-[240px] gap-0 bg-background p-0 sm:max-w-[240px]"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Navigation</SheetTitle>
              <SheetDescription>Organization destinations</SheetDescription>
            </SheetHeader>
            <OrgScopeNav {...orgNavProps} onNavigate={() => setNavOpen(false)} />
          </SheetContent>
        </Sheet>
      )}

      {scope === "project" && isProjectMissionBase(projectBase) && (
        <ProjectUnderlineNav
          projectBase={projectBase}
          issueCount={ctx.loading ? undefined : openIssueCount(ctx.issues)}
          changeCount={ctx.loading ? undefined : countOpenChanges(ctx.reviews)}
        />
      )}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {scope === "org" && (
          <aside className="platform-sidebar hidden w-[240px] shrink-0 flex-col border-r border-border lg:flex">
            <OrgScopeNav {...orgNavProps} />
          </aside>
        )}

        <div
          className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background xl:min-w-[720px]"
          data-testid="mission-main-column"
        >
          {title ? (
            <div className="shrink-0 border-b border-border bg-background px-5 py-3.5">{title}</div>
          ) : null}
          <main className={cn("min-h-0 min-w-0 flex-1 overflow-auto", title ? "p-5" : "px-7 py-6", contentClassName)} aria-busy={ctx.loading || !sessionReady}>
            {children}
          </main>
        </div>
        <TargetHighlight />

        {showAgent && (
          <>
            {!thinkUsesSheet(viewport) && (
              <div className="h-full min-h-0 contents">
                <AgentDock open={agentOpen} onOpenChange={setThinkOpen} />
              </div>
            )}
            {thinkUsesSheet(viewport) && (
              <Sheet
                open={thinkSheetOverlayOpen(viewport, mobileAgent)}
                onOpenChange={setMobileAgent}
              >
                <SheetContent
                  side="right"
                  showCloseButton={false}
                  className="w-[min(100%,320px)] gap-0 bg-background p-0 sm:max-w-[320px]"
                >
                  <SheetHeader className="sr-only">
                    <SheetTitle>Think</SheetTitle>
                    <SheetDescription>Project agent conversation</SheetDescription>
                  </SheetHeader>
                  <AgentDock open fill onOpenChange={() => setMobileAgent(false)} />
                </SheetContent>
              </Sheet>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function PageTitle({
  kicker,
  children,
  extra,
}: {
  kicker?: string;
  children: ReactNode;
  extra?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        {kicker && (
          <div className="mb-0.5 text-[12px] tracking-[0.04em] text-muted-foreground uppercase">{kicker}</div>
        )}
        <h1 className="flex flex-wrap items-center gap-2 text-[18px] leading-tight font-semibold tracking-[0.02em] text-white">
          {children}
        </h1>
      </div>
      {extra}
    </div>
  );
}

export function useProjectNav() {
  const navigate = useNavigate();
  const { orgSlug, projectSlug } = useParams();
  const ctx = useProjectScope();
  const base = ctx.projectBase || (orgSlug && projectSlug ? `/o/${orgSlug}/p/${projectSlug}` : "/");
  return {
    base,
    navigate,
    org: ctx.org ?? { slug: orgSlug || "", name: orgSlug || "" },
    project: ctx.project ?? { slug: projectSlug || "", name: projectSlug || "" },
  };
}
